import React from "react";
import { formatDateTime } from "../../utils/date";
import AnalyticsMiniCard from "./cards/AnalyticsMiniCard";
import AnalyticsSessionTimeline from "./AnalyticsSessionTimeline";
import AnalyticsPaymentHistory from "./AnalyticsPaymentHistory";
import AnalyticsAttributionJourney from "./AnalyticsAttributionJourney";

const S = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const IconX = () => (
    <svg width={16} height={16} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconCalendar = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const IconClock = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const IconLaptop = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="2" y1="20" x2="22" y2="20" />
    </svg>
);
const IconTrendingUp = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);
const IconCircleCheck = ({ size = 11 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...S} aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
    </svg>
);

const accentBar = {
    sky: "from-sky-500 to-blue-500",
    emerald: "from-emerald-500 to-teal-500",
    violet: "from-violet-500 to-purple-500",
};

const Section = ({ title, children, accent = "violet" }) => (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${accentBar[accent]}`} />
        <div className="border-b border-slate-100 px-4 py-2.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h3>
        </div>
        <div className="p-4">{children}</div>
    </div>
);

const MetaChip = ({ label, value }) => (
    <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-widest text-gray-400">{label}</span>
        <span className="text-[12px] text-gray-600">{value || "—"}</span>
    </div>
);

const UserJourneyDrawer = ({ open, onClose, journey }) => {
    if (!open || !journey) return null;

    const { visitor, sessions, timeline, grouped_timeline, payments, attribution_journey } = journey;
    const isConverted = payments?.length > 0;
    const initial = visitor?.display_name?.[0]?.toUpperCase() || "V";

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Visitor journey">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />

            <div className="absolute inset-x-0 bottom-4 top-4 mx-auto flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-white shadow-2xl">
                <div className="flex flex-shrink-0 items-start justify-between border-b border-gray-200 bg-white px-6 py-4">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-[15px] font-semibold text-indigo-600">
                            {initial}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="truncate text-lg font-semibold text-gray-900">
                                    {visitor?.display_name || "Unknown visitor"}
                                </h2>
                                {isConverted && (
                                    <span className="inline-flex items-center gap-1 rounded-md border border-green-100 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                        <IconCircleCheck size={11} />
                                        Converted
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1">
                                <MetaChip label="Browser" value={visitor?.browser} />
                                <MetaChip label="Device" value={visitor?.device} />
                                <MetaChip label="OS" value={visitor?.operating_system} />
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="text-[10px] uppercase tracking-widest text-gray-400">Visitor ID</span>
                                <code className="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-600">
                                    {visitor?.visitor_uuid}
                                </code>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        aria-label="Close drawer"
                        className="ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                    >
                        <IconX />
                    </button>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                    <div className="grid grid-cols-2 gap-3">
                        <AnalyticsMiniCard title="First seen" value={formatDateTime(visitor?.first_seen)} icon={<IconCalendar />} color="blue" />
                        <AnalyticsMiniCard title="Last seen" value={formatDateTime(visitor?.last_seen)} icon={<IconClock />} color="green" />
                        <AnalyticsMiniCard title="Sessions" value={sessions?.length ?? 0} icon={<IconLaptop />} color="orange" />
                        <AnalyticsMiniCard title="Events" value={timeline?.length ?? 0} icon={<IconTrendingUp />} color="indigo" />
                    </div>

                    <AnalyticsAttributionJourney journey={journey.attribution_journey} />

                    <Section title="Visitor journey" accent="violet">
                        <AnalyticsSessionTimeline sessions={grouped_timeline} />
                    </Section>

                    <Section title="Payment history" accent="emerald">
                        <AnalyticsPaymentHistory payments={payments} />
                    </Section>
                </div>
            </div>
        </div>
    );
};

export default UserJourneyDrawer;